import React, { useState } from 'react';
import { Alert, Button, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { errorMessage } from '../../MarketExplorer/api';
import { setCorpus } from './api';
import { CorpusState } from './types';

const colors: Record<CorpusState['state'], string> = {
  off: 'default',
  waking: 'processing',
  ready: 'success',
  error: 'error',
  draining: 'warning',
};
const CorpusControl: React.FC<{
  snapshot: CorpusState | null;
  error: string;
  onChange: (value: CorpusState) => void;
  onRefresh: () => void;
}> = ({ snapshot, error, onChange, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const change = async (enabled: boolean) => {
    if (busy) return;
    setBusy(true);
    setActionError('');
    try {
      onChange(await setCorpus(enabled));
    } catch (reason) {
      setActionError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="corpus-news__control" aria-label="Corpus availability">
      <div className="corpus-news__control-row">
        <div>
          <Space>
            <strong>News corpus</strong>
            <Tag color={snapshot ? colors[snapshot.state] : undefined}>
              {snapshot?.state || 'Checking'}
            </Tag>
            <Tooltip title="This status reflects permission to process corpus work and the last worker check. An idle database can be paused even while this says ready.">
              <InfoCircleOutlined
                tabIndex={0}
                aria-label="About corpus availability"
              />
            </Tooltip>
          </Space>
          <p>{snapshot?.message || 'Reading corpus status…'}</p>
        </div>
        <Space wrap>
          {snapshot?.enabled && snapshot.state === 'error' && (
            <Button
              type="primary"
              loading={busy}
              disabled={!snapshot.configured}
              onClick={() => change(true)}
            >
              Retry wake
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            aria-label="Refresh corpus status"
          />
          {snapshot?.enabled ? (
            <Popconfirm
              title="Turn corpus processing off?"
              description="Queued requests will be canceled. Active batches will stop safely and connections will close."
              onConfirm={() => change(false)}
              okText="Turn off"
            >
              <Button loading={busy}>Turn off</Button>
            </Popconfirm>
          ) : (
            <Button
              type="primary"
              loading={busy}
              disabled={!snapshot?.configured || snapshot?.state === 'draining'}
              onClick={() => change(true)}
            >
              {snapshot?.state === 'error' ? 'Retry wake' : 'Turn on'}
            </Button>
          )}
        </Space>
      </div>
      <div className="corpus-news__status-note">
        Database wakes automatically for work and pauses when idle.{' '}
        {snapshot && (
          <>
            <span>
              {snapshot.capacity.min_acu}–{snapshot.capacity.max_acu} ACU
            </span>
            <span>
              {snapshot.active_jobs} active · {snapshot.pending_jobs} queued
            </span>
          </>
        )}
      </div>
      {(error || actionError || snapshot?.error?.message) && (
        <Alert
          type="error"
          showIcon
          title={actionError || error || snapshot?.error?.message}
        />
      )}
    </section>
  );
};
export default CorpusControl;
